import React, { useEffect, useState } from 'react';

// Magic Bento-style animated background grid
// Enhanced visibility with stronger gradients and varied animations
export default function BentoGrid({ className = '', variant = 'default' }) {
  const [scrollY, setScrollY] = useState(0);
  
  useEffect(() => {
    if (variant === 'subtle') {
      const handleScroll = () => {
        setScrollY(window.scrollY);
      };
      
      // Throttle scroll events
      let ticking = false;
      const scrollHandler = () => {
        if (!ticking) {
          window.requestAnimationFrame(() => {
            handleScroll();
            ticking = false;
          });
          ticking = true;
        }
      };
      
      window.addEventListener('scroll', scrollHandler, { passive: true });
      return () => window.removeEventListener('scroll', scrollHandler);
    }
  }, [variant]);
  // More vibrant gradients with better opacity for visibility
  const tiles = variant === 'subtle' ? [
    // Subtle variant for content-heavy pages
    { id: 1, g: 'from-blue-500/20 to-cyan-400/20', delay: '0s', duration: '15s' },
    { id: 2, g: 'from-purple-500/20 to-pink-400/20', delay: '1s', duration: '18s' },
    { id: 3, g: 'from-emerald-500/20 to-teal-400/20', delay: '2s', duration: '20s' },
    { id: 4, g: 'from-rose-500/20 to-orange-400/20', delay: '0.5s', duration: '16s' },
    { id: 5, g: 'from-indigo-500/20 to-blue-400/20', delay: '1.5s', duration: '19s' },
    { id: 6, g: 'from-fuchsia-500/20 to-violet-400/20', delay: '2.5s', duration: '17s' },
    { id: 7, g: 'from-amber-500/20 to-yellow-400/20', delay: '3s', duration: '21s' },
    { id: 8, g: 'from-sky-500/20 to-indigo-400/20', delay: '0.8s', duration: '14s' },
    { id: 9, g: 'from-lime-500/20 to-emerald-400/20', delay: '1.8s', duration: '22s' },
    { id: 10, g: 'from-blue-500/20 to-purple-400/20', delay: '2.8s', duration: '16s' },
    { id: 11, g: 'from-cyan-500/20 to-teal-400/20', delay: '0.3s', duration: '18s' },
    { id: 12, g: 'from-pink-500/20 to-rose-400/20', delay: '1.3s', duration: '15s' },
  ] : [
    // Default variant with stronger visibility
    { id: 1, g: 'from-blue-500/40 to-cyan-400/40', delay: '0s', duration: '15s' },
    { id: 2, g: 'from-purple-500/40 to-pink-400/40', delay: '1s', duration: '18s' },
    { id: 3, g: 'from-emerald-500/40 to-teal-400/40', delay: '2s', duration: '20s' },
    { id: 4, g: 'from-rose-500/40 to-orange-400/40', delay: '0.5s', duration: '16s' },
    { id: 5, g: 'from-indigo-500/40 to-blue-400/40', delay: '1.5s', duration: '19s' },
    { id: 6, g: 'from-fuchsia-500/40 to-violet-400/40', delay: '2.5s', duration: '17s' },
    { id: 7, g: 'from-amber-500/40 to-yellow-400/40', delay: '3s', duration: '21s' },
    { id: 8, g: 'from-sky-500/40 to-indigo-400/40', delay: '0.8s', duration: '14s' },
    { id: 9, g: 'from-lime-500/40 to-emerald-400/40', delay: '1.8s', duration: '22s' },
    { id: 10, g: 'from-blue-500/40 to-purple-400/40', delay: '2.8s', duration: '16s' },
    { id: 11, g: 'from-cyan-500/40 to-teal-400/40', delay: '0.3s', duration: '18s' },
    { id: 12, g: 'from-pink-500/40 to-rose-400/40', delay: '1.3s', duration: '15s' },
    { id: 13, g: 'from-violet-500/40 to-indigo-400/40', delay: '3.5s', duration: '19s' },
    { id: 14, g: 'from-teal-500/40 to-blue-400/40', delay: '0.7s', duration: '17s' },
    { id: 15, g: 'from-orange-500/40 to-amber-400/40', delay: '2.3s', duration: '20s' },
  ];

  const opacityClass = variant === 'subtle' 
    ? 'opacity-40 dark:opacity-20' 
    : 'opacity-70 dark:opacity-50';

  return (
    <div
      aria-hidden
      className={`bento-grid pointer-events-none absolute inset-0 -z-10 ${opacityClass} ${className}`}
    >
      <div 
        className="bento-grid-inner"
        style={{
          transform: variant === 'subtle' ? `translateY(${scrollY * 0.1}px)` : undefined
        }}
      >
        {tiles.map((t, idx) => (
          <div 
            key={t.id} 
            className={`bento-tile bg-gradient-to-br ${t.g}`}
            style={{
              animationDelay: t.delay,
              animationDuration: t.duration,
              transform: variant === 'subtle' && scrollY > 0 
                ? `translateY(${scrollY * (0.02 * (idx % 3))}px)` 
                : undefined
            }}
          />
        ))}
      </div>
    </div>
  );
}
